import { useState } from "react";
import { apiConfig } from "../authConfig";
import { getUser } from "..";
import CryptoJS from "crypto-js";

interface RequestOptions {
  method: string;
  headers?: Headers;
  body?: Object | FormData | string;
}

export const toJson = (body: Object | FormData | string): string => {
  let data: string;

  // If FormData, convert to object before stringifying.
  if (body instanceof FormData) {
    const obj = formDataToObject(body);
    data = JSON.stringify(obj);
  } else if (body instanceof Object) {
    data = JSON.stringify(body);
  } else {
    data = body;
  }
  return data.trim();
};

export const formDataToObject = (data: FormData): Object => {
  const entries = data.entries();
  let obj: any = {};
  // console.log(data);
  for (let entry of entries) {
    const key = entry[0];
    const val = entry[1];

    // Check if key includes a dot, which indicates it's an object, not a string.
    if (key.includes(".")) {
      let k = key.split(".");
      let objKey = k[0];
      let innerKey = k[1];
      obj[objKey] = { ...obj[objKey], [innerKey]: val };
    } else {
      obj[key] = val;
    }
  }

  return obj;
};

// AES Encryption and Decryption functions
const secretKey = (window as any)?.FL_CARE_AUTH_SECRET_KEY; // Replace this with your secure secret key

// Function to encrypt data using AES-256
function encrypt(data: string, secretKey: string): string {
  const iv = CryptoJS.lib.WordArray.random(16); // Generate random IV (16 bytes)
  const encrypted = CryptoJS.AES.encrypt(
    data,
    CryptoJS.enc.Utf8.parse(secretKey),
    {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }
  );

  // Return the IV and the encrypted data, both in Base64 format
  return iv.toString(CryptoJS.enc.Base64) + ":" + encrypted.toString();
}

const headers: Readonly<Record<string, string | boolean>> = {
  Accept: "application/json",
  "Content-Type": "application/json; charset=utf-8",
  "Access-Control-Allow-Credentials": true,
  "X-Requested-With": "XMLHttpRequest",
};

// Singleton flags to prevent multiple authentication/refresh requests
let isAuthenticating = false;
let isRefreshing = false;

// Function to authenticate and get the access token
async function authenticate(): Promise<string | null> {
  // Check if authentication is already in progress
  if (isAuthenticating) {
    // Wait for the ongoing authentication to complete
    while (isAuthenticating) {
      await new Promise((resolve) => setTimeout(resolve, 100)); // Poll every 100ms
    }
    return sessionStorage.getItem("accessToken");
  }

  isAuthenticating = true; // Set flag to indicate authentication is in progress

  try {
    let token = sessionStorage.getItem("accessToken");
    let refreshToken = sessionStorage.getItem("refreshToken");

    if (token && refreshToken) {
      return token;
    }
    const userEmail = getUser();
    if (!userEmail) {
      console.error("User not authenticated");
    }
    const appCredentials = {
      AppGUID: (window as any)?.FL_CARE_APP_GUID,
      AppName: (window as any)?.FL_CARE_APP_NAME,
      UserEmail: userEmail,
    };

    const encryptedPayload = encrypt(JSON.stringify(appCredentials), secretKey);

    const response = await fetch(
      `${apiConfig.baseUrl}Authentication/authenticate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          encryptedData: encryptedPayload,
        }),
      }
    );

    if (response.ok) {
      const responseData = await response.json();
      sessionStorage.setItem("accessToken", responseData.token);
      sessionStorage.setItem("refreshToken", responseData.refreshToken);
      // Call API After successful authenticate
      const resp = await authenticateSuccessful();
      return responseData.token;
    } else {
      console.error("Failed to authenticate.");
      return null;
    }
  } finally {
    isAuthenticating = false; // Reset flag once authentication is complete
  }
}

async function authenticateSuccessful() {
  try {
    let token = sessionStorage.getItem("accessToken");
    const authHeaders = {
      ...headers,
      Authorization: `Bearer ${token}`,
    };
    const response = await fetch(
      `${apiConfig.baseUrl}Authentication/UserSessionLog`,
      {
        method: "POST",
        headers: authHeaders,
      }
    );
    if (response.ok) {
      const responseData = await response.json();
      return responseData;
    }
  } catch {
    console.error("Unsuccessful Log");
  }
}

// Function to refresh the access token using the refresh token
async function refreshAccessToken(
  currentAccessToken: string
): Promise<string | null> {
  if (isRefreshing) {
    while (isRefreshing) {
      await new Promise((resolve) => setTimeout(resolve, 100)); // Poll every 100ms
    }
    return sessionStorage.getItem("accessToken");
  }

  isRefreshing = true;

  try {
    const refreshToken = sessionStorage.getItem("refreshToken");

    if (!refreshToken) {
      console.error("No refresh token available.");
      return null;
    }

    const response = await fetch(
      `${apiConfig.baseUrl}Authentication/refreshtoken`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: currentAccessToken,
          refreshToken: refreshToken,
        }),
      }
    );

    if (response.ok) {
      const responseData = await response.json();
      sessionStorage.setItem("accessToken", responseData.token);
      sessionStorage.setItem("refreshToken", responseData.refreshToken);
      return responseData.token;
    } else {
      console.error("Failed to refresh token.");
      return null;
    }
  } finally {
    isRefreshing = false; // Reset flag once refresh is complete
  }
}

async function request(url: string, options: RequestOptions) {
  let token = sessionStorage.getItem("accessToken");
  let refreshToken = sessionStorage.getItem("refreshToken");

  if (!token) {
    token = await authenticate(); // Authenticate if no token exists
    if (!token) {
      throw new Error("Authentication failed. No access token received.");
    }
  }

  // Add Authorization header with the access token
  const authHeaders = {
    ...headers,
    Authorization: `Bearer ${token}`,
  };

  let requestOptions: RequestInit = {};
  if (options.method === "GET" || options.method === "DELETE") {
    requestOptions = {
      method: options.method,
      headers: authHeaders,
    };
  } else {
    requestOptions = {
      method: options.method,
      body: toJson(options.body || ""),
      headers: authHeaders,
    };
  }

  try {
    const endPointUrl = `${apiConfig.baseUrl}${url}`;
    let response = await fetch(endPointUrl, requestOptions);

    // If the access token is expired, refresh the token and retry the request
    if (response.status === 401 && refreshToken) {
      token = await refreshAccessToken(token);
      if (!token) {
        throw new Error("Token refresh failed.");
      }
      sessionStorage.setItem("accessToken", token);

      requestOptions.headers = {
        ...authHeaders,
        Authorization: `Bearer ${token}`,
      };

      response = await fetch(endPointUrl, requestOptions);
    }

    if (!response.ok) {
      throw new Error(`Error: ${response.status} - ${response.statusText}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error("API call failed:", error.message);
    throw error;
  }
}

async function requestDocument(url: string, options: RequestOptions) {
  let token = sessionStorage.getItem("accessToken");
  let refreshToken = sessionStorage.getItem("refreshToken");

  if (!token) {
    token = await authenticate(); // Authenticate if no token exists
    if (!token) {
      throw new Error("Authentication failed. No access token received.");
    }
  }

  // Add Authorization header with the access token
  const authHeaders = {
    ...headers,
    Authorization: `Bearer ${token}`,
  };

  let requestOptions: RequestInit = {};
  if (options.method === "GET" || options.method === "DELETE") {
    requestOptions = {
      method: options.method,
      headers: authHeaders,
    };
  } else {
    requestOptions = {
      method: options.method,
      body: toJson(options.body || ""),
      headers: authHeaders,
    };
  }

  try {
    const endPointUrl = `${apiConfig.baseUrl}${url}`;
    let response = await fetch(endPointUrl, requestOptions);

    // If the access token is expired, refresh the token and retry the request
    if (response.status === 401 && refreshToken) {
      token = await refreshAccessToken(token);
      if (!token) {
        throw new Error("Token refresh failed.");
      }
      sessionStorage.setItem("accessToken", token);

      requestOptions.headers = {
        ...authHeaders,
        Authorization: `Bearer ${token}`,
      };

      response = await fetch(endPointUrl, requestOptions);
    }

    if (!response.ok) {
      throw new Error(`Error: ${response.status} - ${response.statusText}`);
    }

    return await response;
  } catch (error: any) {
    console.error("API call failed:", error.message);
    throw error;
  }
}

function sendRequest(
  url: string,
  method: string,
  body?: Object | FormData | string
) {
  if (method === "GET" || method === "DELETE") {
    return request(url, {
      method,
    });
  } else {
    return request(url, {
      method,
      body,
    });
  }
}

function sendRequestDocument(
  url: string,
  method: string,
  body?: Object | FormData | string
) {
  if (method === "GET" || method === "DELETE") {
    return requestDocument(url, {
      method,
    });
  } else {
    return request(url, {
      method,
      body,
    });
  }
}

export default {
  sendRequest,
  sendRequestDocument,
  authenticate,
  refreshAccessToken,
};
